import {
  ModelIdentityMap
} from '../model-identity-map'

import { BaseJsonModelInterface, EntityMappingManager, EntityMapping } from '../'

import { BaseModel } from '../../../models'
import { ModelFactory } from '../../../data/factories'
import { Utils } from '../../../helpers'

export class ModelMapper {

  private _inProgressKey = '__inProgress'
  private _mappedModelRelationshipRegistry: Map<string, Map<string, BaseModel>> = new Map<string, Map<string, BaseModel>>()

  constructor(
    private _modelIdentityMap: ModelIdentityMap,
    private _modelFactory: ModelFactory,
    private _entityMappingManager: EntityMappingManager,
    private _utils: Utils
  ) {}

  /**
   * Creates a model or an array of models based on "normalized" JSON objects
   *
   * @className Name of the class of the model or models to be instantiated
   * @jsonData Normalized JSON data or an array of thereof, that is to be mapped to the object instances
   */
  public mapToModel(className: string, jsonData: any|any[]): BaseModel | BaseModel[] {
    const jsonName = this._entityMappingManager.forClass(className).entityNameForJson
    const mapJsonData = (singleJsonData) => this.mapJson(jsonName, singleJsonData)
    return (Array.isArray(jsonData)) ? jsonData.map(mapJsonData) : mapJsonData(jsonData)
  }

 /**
  * Converts a model(s) to normalized JSON representation
  *
  * @models models which will be converted to jsonArray
  *
  * @returns a json representation of the models
  */
  public mapToJson(models: BaseModel | BaseModel[]): BaseJsonModelInterface | BaseJsonModelInterface[] {
    const processingArray = Array.isArray(models)
    const _models: BaseModel[] = processingArray ? models as BaseModel[] : [models as BaseModel]
    const jsonArray: BaseJsonModelInterface[] = []
    let mapping: EntityMapping
    let property
    let propertyJson
    let json

    for (const model of _models) {
      mapping = this._entityMappingManager.forClass(model.type)
      json = {}

      for (const propertyName in model) {
        property = model[propertyName]
        // Do not map functions or excluded attributes
        if (
          typeof property === 'function' ||
          this._isToJsonPropertyExcludedFromMapping(propertyName)
        ) {
          continue
        }

        // Do not map relationships that have been mapped
        if (this._isRelationship(property) && this._hasRelationBeenMapped(model, propertyName)) {
          return
        }

        if (property instanceof BaseModel) {
          propertyJson = this._mapRelationshipToJson(mapping, property, propertyName)
        } else if (Array.isArray(property) && !mapping.hasCustomJsonMapping(propertyName)) {
          propertyJson = this._mapRelationshipsToJson(mapping, property, propertyName)
        } else {
          propertyJson = this._mapSimpleAttributeToJson(mapping, property, propertyName)
        }

        // Merge propeties together
        json = Object.assign(json, propertyJson)
      }

      json['type'] = mapping.entityNameForJson
      jsonArray.push(json)

      this._mappedModelRelationshipRegistry.delete(model.hashKey)
    }

    return processingArray ? jsonArray : jsonArray[0]
  }

  /**
   * Merges a single model with "normalized" JSON data that describes the model
   *
   * @param model Model that corresponds to the `jsonData` and to which the `jsonData` should be mapped/merged
   * @param jsonData "Normalized" JSON data that should be merged with the model
   */
  public mergeModel(model: BaseModel, jsonData: any): BaseModel {
    if (!(model instanceof BaseModel && typeof jsonData === 'object' && !(Array.isArray(jsonData)))) {
      throw new Error('Type mismatch. Unable to merge.')
    }
    return this.merge(model, jsonData) as BaseModel
  }

  /**
   * Merges an array of (all-of-the-same-type) models with an array of "normalized" JSON data that describes the models
   *
   * @param models Array of models that corresponds to the `jsonData` and to which the `jsonData` should be mapped/merged
   * @param jsonData Array of "normalized" JSON data that should be merged with the model
   */
  public mergeModels(models: BaseModel[], jsonData: any[]): BaseModel[] {
    if (!(Array.isArray(models) && Array.isArray(jsonData) && models.length === jsonData.length)) {
      throw new Error('Opposing attribute lengths. Unable to merge.')
    }
    let firstClassName
    const allClassesTheSame = models.every((model) => {
      if (!(model instanceof BaseModel)) { return false }
      if (typeof firstClassName === 'undefined') { firstClassName = model.type }
      return firstClassName === model.type
    })

    if (!allClassesTheSame) {
      throw new Error('Unlike class types. Cannot merge.')
    }
    return models.map((model, i) => this.mergeModel(model, jsonData[i]))
  }

  private merge(model: BaseModel|BaseModel[], jsonData: any|any[]): BaseModel|BaseModel[] {
    const ensureModelInIdMap = (_model: BaseModel, _jsonData: any) => {
      if (typeof _model.id === 'undefined') {
        this._modelFactory.setPropertyOnModel(_model, 'id', jsonData.id)
        this._modelIdentityMap.setModel(_model)
      }

      if (_model.id !== _jsonData.id) {
        throw new Error('Identifier mismatch. Unable to merge.')
      }

      const existingModel = this._modelIdentityMap.getModel(_model.type, _model.id)
      if (typeof existingModel === 'undefined') {
        throw new Error('Model is not present in identity map. Unable to merge.')
      }
    }

    const modelClassName = (Array.isArray(model)) ? model[0].type : model.type
    if (Array.isArray(model)) {
      model.forEach((_model: BaseModel, i: number) => ensureModelInIdMap(_model, jsonData[i]))
    } else {
      ensureModelInIdMap(model, jsonData)
    }
    return this.mapToModel(modelClassName, jsonData)
  }

  private mapJson(jsonType: string, jsonData: object): BaseModel {
    const modelId = jsonData['id']
    if (!modelId) {
      throw new Error(`The data of type "${jsonType}" cannot be mapped, because it does not have an ID`)
    }

    const entityMapping = this._entityMappingManager.forJson(jsonType)
    const supportedClassName = this._modelFactory.normalizedClassName(entityMapping.entityNameForClass)

    if (! supportedClassName) {
      throw new Error(`Cannot instantiate Model for entity "${entityMapping.entityNameForClass}";
      there's no supported implementation to be used for creation`)
    }

    const mappedModel = this.createOrReuseModelInstance(supportedClassName, modelId)

    if (this._isModelBeingMapped(mappedModel)) {
      return mappedModel
    }

    this._setModelBeingMapped(mappedModel)

    const jsonProperties = Object.keys(jsonData)
    for (const modelDataPropertyName of jsonProperties) {
      if (this._isPropertyExcludedFromMapping(modelDataPropertyName)) { continue }

      const modelPropertyName = entityMapping.getClassPropertyName(modelDataPropertyName)
      const modelDataPropertyValue = jsonData[modelDataPropertyName]

      // this assumes that the data from the server determines whether it's a collection/relation etc.
      if (Array.isArray(modelDataPropertyValue)) {
        this.mapRelations(mappedModel, modelPropertyName, modelDataPropertyValue)
      } else if (this._isModelData(modelDataPropertyValue)) {
        this.mapRelation(mappedModel, modelPropertyName, modelDataPropertyValue)
      } else {
        let value = this._isJsonApiObject(modelDataPropertyValue) ? null : modelDataPropertyValue
        value = entityMapping.getClassTransformedValue(modelDataPropertyName, value)
        this.mapPrimitiveAttribute(mappedModel, modelPropertyName, value)
      }
    }

    this._unsetModelBeingMapped(mappedModel)
    mappedModel['_persisted'] = true
    return mappedModel
  }

  private mapPrimitiveAttribute(model: BaseModel, attributeName: string, propertyValue: any): void {

    const currentPropertyValue = model[attributeName]
    if (typeof currentPropertyValue !== 'undefined' && model.isPersisted()) {
      this.resolveAttributeCollision(model, attributeName, currentPropertyValue, propertyValue)
    } else {
      this._modelFactory.setPropertyOnModel(model, attributeName, propertyValue)
    }
  }

  private _isJsonApiObject(property) {
    return typeof property === 'object' && property !== null && ('_meta' in property || '_links' in property)
  }

  private resolveAttributeCollision(model: BaseModel, attributeName: string, currentValue: any, newValue: any): void {
    const value = this._utils.isValueEmpty(newValue) ? currentValue : newValue
    this._modelFactory.setPropertyOnModel(model, attributeName, value)
  }

  private mapRelations(model: BaseModel, relationName: string, modelData: any[]): void {
    const currentRelations = model[relationName]
    if (Array.isArray(currentRelations) && currentRelations.length > 0 && model.isPersisted()) {
      this.resolveRelationsCollision(model, relationName, currentRelations, modelData)
    } else {
      // Type info would be lost here using yayson, but we have a monkey patched Store that can provide it
      const mappedData = modelData.map((relatedData) => this._isModelData(relatedData)
        ? this.mapJson(relatedData.type, relatedData) : relatedData)
      this._modelFactory.setPropertyOnModel(model, relationName, mappedData)
    }
  }

  private resolveRelationsCollision(
    model: BaseModel, relationName: string, currentValues: BaseModel[], newValuesData: any[]): void {
    // Merge new relations in

    // Add those models, which haven't made it into the collection yet
    // Update the ones, which are in there
    // Remove nothing => This current implementation is a true merge in fact
    const currentRelatedObjectIds = currentValues.reduce((map, _model) => {map.set(_model.id, true); return map}, new Map())

    newValuesData.forEach((modelData) => {
      // Type info would be lost here using yayson, but we have a monkey patched Store that can provide it
      const relatedModel = this._isModelData(modelData) ? this.mapJson(modelData.type, modelData) : modelData
      if (!currentRelatedObjectIds.get(modelData.id)) { // only add the model if it does not exist
        model[relationName].push(relatedModel)
      }
    })
  }

  private mapRelation(model: BaseModel, relationName: string, modelData: any): void {
    const currentRelationValue = model[relationName]
    if (currentRelationValue && currentRelationValue.id !== undefined && model.isPersisted()) {
      this.resolveRelationCollision(model, relationName, currentRelationValue, modelData)
    } else {
      // Type info would be lost here using yayson, but we have a monkey patched Store that can provide it
      this._modelFactory.setPropertyOnModel(model, relationName, this.mapJson(modelData.type, modelData))
    }
  }

  private resolveRelationCollision(
    _model: BaseModel,
    _relationName: string,
    currentValue: BaseModel,
    newValueData: any
  ): void {
    // Keep the current object in place
    // Update the current object if both are identical (same ID)
    if (currentValue.id === newValueData.id) {
      // Type info would be lost here using yayson, but we have a monkey patched Store that can provide it
      this.mapJson(newValueData.type, newValueData)
    }
  }

  private createOrReuseModelInstance(className: string, id: string) {
    let modelInstance = this._modelIdentityMap.getModel(className, id)

    if (modelInstance) {
      if (!modelInstance.isPersisted()) {
        modelInstance['_persisted'] = true
      }
      return modelInstance
    }

    modelInstance = this._modelFactory.instantiateClass(className)
    this._modelFactory.setPropertyOnModel(modelInstance, 'id', id)
    this._modelIdentityMap.setModel(modelInstance)
    return modelInstance
  }

  private _mapSimpleAttributeToJson(mapping: EntityMapping, propertyValue: any, propertyName: string): {string: any} {
    const json = {}
    json[mapping.getJsonPropertyName(propertyName)] = mapping.getJsonTransformedValue(propertyName, propertyValue)
    return json as {string: any}
  }

  private _mapRelationshipToJson(
    mapping: EntityMapping,
    model: BaseModel,
    propertyName: string
  ): {string: BaseJsonModelInterface} {
    const json = {}
    const propertyJsonKey = mapping.getJsonPropertyName(propertyName, this._customRelationshipJsonNameStrategy(model.type))
    json[propertyJsonKey] = this.mapToJson(model)
    return json as {string: BaseJsonModelInterface}
  }

  private _mapRelationshipsToJson(
    mapping: EntityMapping,
    models: BaseModel[],
    propertyName: string
  ): {string: BaseJsonModelInterface[]} {
    const json = {}
    const type = (models[0] || {type: ''}).type
    const propertyJsonKey = mapping.getJsonPropertyName(
      propertyName,
      this._customRelationshipJsonNameStrategy(type)
    )
    json[propertyJsonKey] = this.mapToJson(models) as BaseJsonModelInterface[]

    return json as {string: BaseJsonModelInterface[]}
  }

  private _customRelationshipJsonNameStrategy(type: string) {
    return () => this._utils.singularize(this._entityMappingManager.forClass(type).entityNameForJson)
  }

  private _isPropertyExcludedFromMapping(name) {
    return ['id', 'type', 'get', 'links', 'relationships', '_links', '_meta'].indexOf(name.toLowerCase()) !== -1
  }

  private _isToJsonPropertyExcludedFromMapping(name: string): boolean {
    return ['type', 'hashkey'].indexOf(name.toLowerCase()) > 0
  }

  private _isModelData(jsonModelData: any) {
    return (typeof jsonModelData === 'object' && jsonModelData !== null && typeof jsonModelData.type === 'string')
  }

  private _isModelBeingMapped(model: BaseModel): boolean {
    return model[this._inProgressKey]
  }

  private _setModelBeingMapped(model: BaseModel) {
    model[this._inProgressKey] = true
  }

  private _unsetModelBeingMapped(model: BaseModel) {
    delete model[this._inProgressKey]
  }

  private _hasRelationBeenMapped(model: BaseModel, propertyName: string): boolean {
    const childModel = model[propertyName]
    let modelMappedRelationship = this._mappedModelRelationshipRegistry.get(model.hashKey)
    if (modelMappedRelationship && modelMappedRelationship.has(propertyName)) {
      return true
    } else if (! modelMappedRelationship) {
      modelMappedRelationship = new Map<string, BaseModel>()
      this._mappedModelRelationshipRegistry.set(model.hashKey, modelMappedRelationship)
    }
    modelMappedRelationship.set(propertyName, childModel)

    return false
  }

  private _isRelationship(property): boolean {
    return property instanceof BaseModel || Array.isArray(property)
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/backends/model-mapper/model-mapper.ts